import { cx } from 'emotion';
import {
  CSSProperties,
  memo,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AMOUNT_TYPES } from '@ezugi/constants';
import { useOrientationChange } from '@ezugi/hooks/src/useOrientationChange';
import payoutsIcon from '@ezugi/static/icons/payouts.svg';
import { GameFooter } from '@ezugi/ui/components/GameFooter/GameFooter';
import { DeviceInfo, formatAmount } from '@ezugi/utils';

import { useAmount } from '../../hooks/useAmount';
import { useIntl } from '../../hooks/useIntl';
import { menuActions } from '../../store/actions';
import { ANALYTICS } from '../../store/constants';
import { FormatConfig } from '../../store/reducers/config';
import { MenuSetPayload } from '../../store/reducers/menu';
import {
  formatSelector,
  isCashierEnabledSelector,
  isNetPositionVisibleSelector,
  lastWinSelector,
  netPositionValueSelector,
  roundIdSelector,
  userCurrencySelector,
  userRealtimeBalanceSelector,
} from '../../store/selectors';
import TableLimits from './components/TableLimits';
import WinAmount from './components/WinAmount';
import {
  balanceAndTotalBetWrapper,
  balanceWrapper,
  betInfoContainer,
  netPositionDesktopClassName,
  netPositionMobileLandscapeClassName,
  netPositionMobilePortraitLeftClassName,
  clickable,
  middleLegacy,
  rightSideWrapper,
  middleLegacyWithNetPosition,
  leftSideWrapper,
} from './style.css';
import {
  displayBalance,
  setSpecificAmountFormatTypes,
} from './utils';

const { cashier, menu } = menuActions;

const intlSpec = {
  keys: {
    balanceLabel: 'general.balance',
    payoutLabel: 'games_ui.limits_and_payouts',
  },
};

export const GAME_FOOTER_TEST_ID = 'game-footer';

export type GameFooterProps = {
  children?: ReactNode;
  className?: string;
  legacy?: boolean;
  showSeconds?: boolean;
  gradient?: boolean;
  style?: CSSProperties;
};

const NetPosition = ({ className }: { className?: string }) => {
  const orientation = useOrientationChange();
  const isPortraitSpecific = DeviceInfo.isHandheld && orientation === 'portrait';

  const { netPositionLabel } = useIntl({
    keys: {
      netPositionLabel: 'games_ui.net_position',
    },
  });

  const netPositionValue = useSelector(netPositionValueSelector);

  const { netPositionFormattedValue } = useAmount({
    ...(isPortraitSpecific && {
      format: {
        betFormat: setSpecificAmountFormatTypes(netPositionValue),
      },
    }),
    values: {
      netPositionFormattedValue: {
        value: netPositionValue,
        type: 'bet',
        showCurrencySymbol: true,
        showCurrencyCode: false,
      },
    },
  });

  return (
    <GameFooter.Text
      className={className}
      label={netPositionLabel}
      value={netPositionFormattedValue}
      data-e2e="net-position"
      boldValue={false}
      transparentValue={true}
      desktopOpacity={0.5}
    />
  );
};

const LastWin = () => {
  const lastWin = useSelector(lastWinSelector);

  const { lastWinAmount } = useAmount({
    values: {
      lastWinAmount: {
        value: lastWin,
        type: 'bet',
      },
    },
  });

  const { lastWinLabel } = useIntl({
    keys: {
      lastWinLabel: 'general.last_win',
    },
  });

  return <GameFooter.Text data-e2e="last-win" label={lastWinLabel} value={lastWinAmount} />;
};

const GameFooterBootstrap = ({
  children,
  className,
  legacy = true,
  showSeconds = true,
  gradient = false,
  style,
}: GameFooterProps) => {
  const realtimeBalance = useSelector(userRealtimeBalanceSelector);
  const isCashierEnabled = useSelector(isCashierEnabledSelector);
  const format = useSelector(formatSelector) as FormatConfig;
  const currency = useSelector(userCurrencySelector);
  const roundId = useSelector(roundIdSelector);

  const dispatch = useDispatch();

  const orientation = useOrientationChange();
  const isPortraitSpecific = DeviceInfo.isHandheld && orientation === 'portrait';
  const isLandscapeSpecific = DeviceInfo.isHandheld && orientation === 'landscape';

  const { balanceLabel, payoutLabel } = useIntl(intlSpec);
  const isNetPositionVisible = useSelector(isNetPositionVisibleSelector);

  const setCustomFormatConfig = useCallback(
    (amount: number) => ({
      ...format,
      betFormat: isPortraitSpecific
        ? setSpecificAmountFormatTypes(amount)
        : format.betFormat,
    }),
    [ format, isPortraitSpecific ]
  );

  const [ balanceAmount ] = useMemo(
    () => [
      formatAmount(
        realtimeBalance,
        AMOUNT_TYPES.BET,
        setCustomFormatConfig(realtimeBalance),
        currency
      ),
    ],
    [ realtimeBalance, setCustomFormatConfig, currency ]
  );

  const displayedBalance = useMemo(
    () => displayBalance(realtimeBalance, balanceAmount),
    [ realtimeBalance, balanceAmount ]
  );

  const isBalanceClickable = !DeviceInfo.isHandheld && isCashierEnabled;
  const onBalanceClick = useCallback(() => {
    if (isCashierEnabled) {
      dispatch(
        cashier.request({ source: ANALYTICS.CASHIER_SOURCE.GAME_FOOTER })
      );
    }
  }, [ dispatch, isCashierEnabled ]);

  const isTableNameClickable = !DeviceInfo.isHandheld;
  const onTableNameClick = useCallback(
    () => dispatch(
      menu.set({
        widget: {
          key: 'payouts',
          'data-e2e': 'menu_link_payouts',
          icon: payoutsIcon,
          onClick: () => {},
          drawer: true,
          label: payoutLabel,
        },
      } as MenuSetPayload)
    ),
    [ dispatch, payoutLabel ]
  );

  if (legacy && !DeviceInfo.isHandheld) {
    return (
      <GameFooter
        data-e2e={GAME_FOOTER_TEST_ID}
        data-testid={GAME_FOOTER_TEST_ID}
        className={className}
        style={style}
      >
        {children}
        <div
          className={leftSideWrapper}
          style={{ cursor: isTableNameClickable ? 'pointer' : 'auto' }}
          {...(isTableNameClickable && { onClick: onTableNameClick })}
        >
          <TableLimits />
        </div>

        <div className={isNetPositionVisible ? middleLegacyWithNetPosition : middleLegacy}>
          <div
            data-e2e="balance-button"
            className={cx(balanceWrapper, {
              [clickable]: isBalanceClickable,
            })}
            {...(isBalanceClickable && { onClick: onBalanceClick })}
          >
            <GameFooter.Text
              data-e2e="balance"
              label={balanceLabel}
              value={displayedBalance}
              data-true-value={realtimeBalance}
            />
          </div>
          <WinAmount />
          <LastWin />

          {isNetPositionVisible && <NetPosition />}

        </div>

        {roundId && (
          <div className={rightSideWrapper}>
            <GameFooter.RoundInfo legacy={true} roundId={roundId} showSeconds={showSeconds} />
          </div>
        )}
      </GameFooter>
    );
  }

  return (
    <GameFooter
      className={className}
      gradient={gradient}
      data-e2e={GAME_FOOTER_TEST_ID}
      data-testid={GAME_FOOTER_TEST_ID}
      style={style}
    >
      <GameFooter.Left
        className={cx({
          [netPositionMobilePortraitLeftClassName]:
            DeviceInfo.isHandheld
            && orientation === 'portrait'
            && isNetPositionVisible,
        })}
      >
        <div className={betInfoContainer}>
          <div className={balanceAndTotalBetWrapper}>
            <div
              className={cx(balanceWrapper, {
                [clickable]: isBalanceClickable,
              })}
              {...(isBalanceClickable && { onClick: onBalanceClick })}
            >
              <GameFooter.Text
                data-e2e="balance"
                label={balanceLabel}
                value={displayedBalance}
                data-true-value={realtimeBalance}
              />
            </div>
            {isNetPositionVisible && isPortraitSpecific && <NetPosition />}

            <WinAmount />
          </div>
          {isNetPositionVisible && !DeviceInfo.isHandheld && (
            <NetPosition className={netPositionDesktopClassName} />
          )}
        </div>
      </GameFooter.Left>

      {children}

      <GameFooter.Right
        isClickable={isTableNameClickable}
        {...(isTableNameClickable && { onClick: onTableNameClick })}
      >
        <TableLimits />

        {isNetPositionVisible && isLandscapeSpecific && (
          <NetPosition className={netPositionMobileLandscapeClassName} />
        )}

        {roundId && (
          <GameFooter.RoundInfo legacy={false} roundId={roundId} showSeconds={showSeconds} />
        )}
      </GameFooter.Right>
    </GameFooter>
  );
};

export default memo(GameFooterBootstrap);
